import { mbeService } from './mbeService';
import { meeService } from './meeService';
import { mptService } from './mptService';
import { supabase } from '../api/client';

/**
 * Unified Exam Service
 * Provides a single entry point for all exam-related services
 */
class ExamService {
  constructor() {
    this.mbe = mbeService;
    this.mee = meeService;
    this.mpt = mptService;
  }

  /**
   * Get exam tickets for a user
   */
  async getUserExamTickets(userId) {
    try {
      const { data, error } = await supabase
        .from('user_exam_tickets')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching exam tickets:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Use an exam ticket
   */
  async useExamTicket(userId, examType) {
    try {
      // Check if user has available tickets
      const { data: tickets, error: fetchError } = await supabase
        .from('user_exam_tickets')
        .select('*')
        .eq('user_id', userId)
        .eq('exam_type', examType)
        .gt('remaining_count', 0)
        .single();

      if (fetchError) throw fetchError;

      if (!tickets) {
        throw new Error('No available tickets for this exam type');
      }

      // Decrement ticket count
      const { data, error } = await supabase
        .from('user_exam_tickets')
        .update({ remaining_count: tickets.remaining_count - 1 })
        .eq('id', tickets.id)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error using exam ticket:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get exam history for a user
   */
  async getExamHistory(userId, examType = null) {
    try {
      let query = supabase
        .from('exam_attempts')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (examType) {
        query = query.eq('exam_type', examType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching exam history:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save exam attempt
   */
  async saveExamAttempt(attemptData) {
    try {
      const { data, error } = await supabase
        .from('exam_attempts')
        .insert([attemptData])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving exam attempt:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get overall statistics
   */
  async getOverallStats(userId) {
    try {
      const [mbeStats, meeStats, mptStats] = await Promise.all([
        this.mbe.getUserProgress(userId),
        this.mee.getUserProgress(userId),
        this.mpt.getUserProgress(userId),
      ]);

      const stats = {
        mbe: mbeStats.success ? mbeStats.data.stats : null,
        mee: meeStats.success ? meeStats.data.stats : null,
        mpt: mptStats.success ? mptStats.data.stats : null,
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error fetching overall stats:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const examService = new ExamService();
export default examService;